/**
 * ProductTypeForm.tsx — the product type add / edit modal.
 *
 * Shared by Admin > Product Types and the Add / Update buttons beside the
 * product type selector on Admin > Products, so both save the same fields the
 * same way. Functionally the corporate dashboard's product type form, single
 * account only:
 *   - name, type (Product / Credit), disbursal type (switching to Retail asks
 *     first, with the corporate dashboard's warning), equivalent credits (only
 *     for a Product), description, plan types, categories and the image.
 *   - Categories are picked from the read-only /product-categories-lookup
 *     list; that list itself is not editable in Prospera.
 *
 * Laid out like the corporate dashboard's Product Types edit dialog: the image
 * tile and Categories in a left column; Type Name, Type, Disbursal Type,
 * Equivalent Credits, Description and Plan Types on the right (categories, credits
 * and plan types picked from a searchable multi-select with the chosen ones
 * listed under it, each removable); the attached products / appointment types as collapsible lists
 * below; required items listed above the footer, Create / Update disabled
 * until they are met. There is no Delete here: it stays on the list row.
 *   - Names are unique per account, deleted types included. A clash is
 *     blocked while typing; the API's DUPLICATE_PRODUCT_TYPE_NAME is the
 *     backstop. An edit is only checked when the name actually changes, so
 *     rows that already share a name stay editable.
 *   - With `showAttachments`, an edit also lists (read-only) the products and
 *     appointment types using the type.
 *
 * Escape does not close it: the image picker opens on top, and every open
 * Modal listens for Escape on document, so one key would close both.
 *
 * On a Locked Admin page (useAdminPageLevel) it is a viewer, as AdminFormModal
 * is: the fields are read-only (AdminLockedFieldset), the title drops "Edit",
 * the footer is a single Close, and closing never asks to discard.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarCheck, ChevronDown, ChevronRight, Info, Package, RotateCcw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { RichDescriptionField } from './RichTextDialog';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EntityImageTile } from '@/components/admin/EntityImageTile';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  DialogField,
  DialogFooterLayout,
  DialogHeading,
  LABEL_CLASS,
  SelectedRows,
  fieldClasses,
  type FieldState,
} from '@/components/admin/DialogChrome';
import { toast } from '@/store/toastStore';
import { getErrorCode, getErrorMessage } from '@/services/apiClient';
import * as typesSvc from '@/services/productTypes.service';
import type {
  AttachedAppointmentType,
  PlanTypeOption,
  ProductTypeAttachedProduct,
  ProductTypeDetail,
  ProductTypePayload,
} from '@/services/productTypes.service';
import * as inventorySvc from '@/services/inventory.service';
import type { ProductCategoryLookupEntry, ProductTypeLookupEntry } from '@/services/inventory.service';
import { assignEntityImage, getDefaultEntityImageUrl } from '@/services/entityImages.service';
import { formatCurrency, parseCurrency } from '@/lib/utils';

// ─── Shared rules ──────────────────────────────────────────────────────────────

/** Trimmed, lower-cased: how the API compares product type names. */
export function productTypeNameKey(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

export function isDeletedProductType(t: Pick<ProductTypeLookupEntry, 'status'>): boolean {
  return t.status !== undefined && t.status !== null && Number(t.status) === 0;
}

export function isCreditProductType(t: Pick<ProductTypeLookupEntry, 'productCredits'>): boolean {
  return String(t.productCredits ?? '0') === '1';
}

/**
 * The product type in `rows` already carrying `name`, or undefined. A live
 * match wins over a deleted one so the message names the one in use.
 */
export function findProductTypeNameConflict(
  rows: ProductTypeLookupEntry[],
  name: string,
  excludeId?: number | null,
): ProductTypeLookupEntry | undefined {
  const key = productTypeNameKey(name);
  if (!key) return undefined;
  const matches = rows.filter(
    (r) => productTypeNameKey(r.productTypeName) === key && (excludeId == null || r.productTypeID !== excludeId),
  );
  return matches.find((r) => !isDeletedProductType(r)) ?? matches[0];
}

/** The inline sentence for a taken name. */
export function productTypeConflictMessage(typed: string, match: ProductTypeLookupEntry): string {
  const name = typed.trim();
  return isDeletedProductType(match)
    ? `This account already has a deleted product type named "${name}" (#${match.productTypeID}). Restore it instead of adding another.`
    : `This account already has a product type named "${name}" (#${match.productTypeID}). Pick it from the list instead of adding another.`;
}

/** The corporate dashboard's warning before switching to Payment (Retail) disbursal. */
export const RETAIL_DISBURSAL_WARNING = [
  'Payment Disbursal (Retail) should only be used for retail products that will not be assigned to appointments.',
  'Products with this disbursal type cannot be attached to appointment types and will not have an audit trail linking them to specific services.',
  'Appointment Disbursal is recommended for most product types as it tracks which products were used for each service.',
  'Are you sure you want to switch to Payment Disbursal?',
];

// ─── Form data ─────────────────────────────────────────────────────────────────

export interface ProductTypeFormData {
  name: string;
  /** 0 = Product, 1 = Credit. */
  type: 0 | 1;
  /** 0 = Appointment, 1 = Payment (retail). */
  disburseType: 0 | 1;
  description: string;
  equivalentCreditIds: number[];
  planTypeIds: number[];
  categoryIds: number[];
}

export function emptyProductTypeForm(): ProductTypeFormData {
  return { name: '', type: 0, disburseType: 0, description: '', equivalentCreditIds: [], planTypeIds: [], categoryIds: [] };
}

export function formFromProductTypeDetail(d: ProductTypeDetail): ProductTypeFormData {
  return {
    name: d.productTypeName ?? '',
    type: String(d.productCredits) === '1' ? 1 : 0,
    disburseType: Number(d.disburseType) === 1 ? 1 : 0,
    description: d.description ?? '',
    equivalentCreditIds: [...d.productTypeCreditID],
    planTypeIds: [...d.associationID],
    categoryIds: [...d.prodcategory],
  };
}

export function productTypePayload(form: ProductTypeFormData): ProductTypePayload {
  return {
    productTypeName: form.name.trim(),
    type: form.type,
    disburseType: form.disburseType,
    description: form.description,
    categoryIds: form.categoryIds,
    planTypeIds: form.planTypeIds,
    // Equivalent credits only mean something on a Product.
    equivalentCredits: form.type === 0 ? form.equivalentCreditIds.map((id) => ({ productTypeId: id, quantity: 1 })) : [],
  };
}

function sameIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.every((v, i) => v === sb[i]);
}

function sameForm(a: ProductTypeFormData, b: ProductTypeFormData): boolean {
  return (
    a.name === b.name &&
    a.type === b.type &&
    a.disburseType === b.disburseType &&
    a.description === b.description &&
    sameIds(a.equivalentCreditIds, b.equivalentCreditIds) &&
    sameIds(a.planTypeIds, b.planTypeIds) &&
    sameIds(a.categoryIds, b.categoryIds)
  );
}

function toggleId(ids: number[], id: number, on: boolean): number[] {
  return on ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((x) => x !== id);
}

const TYPE_OPTIONS: { value: 0 | 1; label: string }[] = [
  { value: 0, label: 'Product' },
  { value: 1, label: 'Credit' },
];

const DISBURSE_OPTIONS: { value: 0 | 1; label: string }[] = [
  { value: 0, label: 'Appointment Disbursal (Recommended)' },
  { value: 1, label: 'Payment Disbursal (Retail)' },
];

// ─── Modal ─────────────────────────────────────────────────────────────────────

export interface ProductTypeFormModalProps {
  /** null = a new product type. */
  productTypeId: number | null;
  /** Every product type in the account, deleted ones too (duplicate check, credit options). */
  allTypes: ProductTypeLookupEntry[];
  /** List the products and appointment types using the type (edit only). */
  showAttachments?: boolean;
  attachedAppointmentTypes?: AttachedAppointmentType[];
  /** A new type's starting name (corp prefills it with the product's name). */
  initialName?: string;
  onClose: () => void;
  /** After a successful save; `imageUrl` is the type's image (or null). */
  onSaved: (saved: ProductTypeDetail, isNew: boolean, imageUrl: string | null) => void;
  /** An existing type's image changed (the picker saves it on the spot). */
  onImageChanged?: (productTypeId: number, url: string) => void;
}

/** The (i) beside a box label; its text is the corporate dashboard's tooltip. */
function InfoTip({ text }: { text: string }) {
  return (
    <span className="inline-flex cursor-help" title={text} aria-label={text} role="img">
      <Info className="h-3.5 w-3.5 text-slate-400" aria-hidden />
    </span>
  );
}

export function ProductTypeFormModal({
  productTypeId,
  allTypes,
  showAttachments = false,
  attachedAppointmentTypes = [],
  initialName,
  onClose,
  onSaved,
  onImageChanged,
}: ProductTypeFormModalProps) {
  const isNew = productTypeId === null;
  const locked = useAdminPageLevel() === 'locked';
  const startForm = (): ProductTypeFormData => ({ ...emptyProductTypeForm(), name: initialName?.trim() ?? '' });
  const [form, setForm] = useState<ProductTypeFormData | null>(isNew ? startForm() : null);
  const [saved, setSaved] = useState<ProductTypeFormData | null>(isNew ? startForm() : null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [planTypes, setPlanTypes] = useState<PlanTypeOption[]>([]);
  const [categories, setCategories] = useState<ProductCategoryLookupEntry[]>([]);
  const [products, setProducts] = useState<ProductTypeAttachedProduct[] | null>(null);
  const [nameError, setNameError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [confirmRetail, setConfirmRetail] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [openSections, setOpenSections] = useState<Set<'products' | 'appts'>>(new Set(['products', 'appts']));

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([typesSvc.listPlanTypeOptions(), inventorySvc.listProductCategoriesLookup()]).then(
      ([plans, cats]) => {
        if (cancelled) return;
        setPlanTypes(plans.status === 'fulfilled' && Array.isArray(plans.value) ? plans.value : []);
        setCategories(cats.status === 'fulfilled' && Array.isArray(cats.value) ? cats.value : []);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (productTypeId === null) return;
    let cancelled = false;
    typesSvc
      .getProductType(productTypeId)
      .then((d) => {
        if (cancelled) return;
        const f = formFromProductTypeDetail(d);
        setForm(f);
        setSaved(f);
      })
      .catch(() => {
        if (!cancelled) setDetailFailed(true);
      });
    getDefaultEntityImageUrl('product-type', productTypeId)
      .then((url) => {
        if (!cancelled) setImageUrl(url);
      })
      .catch(() => undefined);
    if (showAttachments) {
      typesSvc
        .listProductTypeProducts(productTypeId)
        .then((rows) => {
          if (!cancelled) setProducts(rows.filter((p) => p.active));
        })
        .catch(() => {
          if (!cancelled) setProducts([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [productTypeId, showAttachments]);

  const dirty = !isNew && form !== null && saved !== null && !sameForm(form, saved);

  // Only a rename can create a new clash; a new type is always checked.
  const conflict = useMemo(() => {
    if (!form) return undefined;
    if (!isNew && saved && productTypeNameKey(form.name) === productTypeNameKey(saved.name)) return undefined;
    return findProductTypeNameConflict(allTypes, form.name, productTypeId);
  }, [allTypes, form, saved, isNew, productTypeId]);

  const blankName = form !== null && form.name.trim() === '';
  const shownNameError = nameError ?? (conflict && form ? productTypeConflictMessage(form.name, conflict) : undefined);
  const nameState: FieldState = blankName || shownNameError ? 'error' : 'none';
  const requiredErrors = form === null ? [] : blankName ? ['Type name is required'] : conflict ? ['Type name is already used'] : [];

  function patch(next: Partial<ProductTypeFormData>) {
    setForm((prev) => (prev ? { ...prev, ...next } : prev));
    if ('name' in next) setNameError(undefined);
  }

  function requestClose() {
    if (saving) return;
    if (dirty && !locked) setConfirmDiscard(true);
    else onClose();
  }

  function revertAll() {
    if (saved) setForm({ ...saved });
    setNameError(undefined);
  }

  function changeDisburse(v: 0 | 1 | null) {
    if (!form || v === null || v === form.disburseType) return;
    if (v === 1) setConfirmRetail(true);
    else patch({ disburseType: 0 });
  }

  async function handleSave() {
    if (!form || locked) return;
    // The footer lists these and disables the button; checked here too.
    if (form.name.trim() === '' || conflict) return;
    setSaving(true);
    try {
      const payload = productTypePayload(form);
      const result =
        productTypeId === null
          ? await typesSvc.createProductType(payload)
          : await typesSvc.updateProductType(productTypeId, payload);
      let finalImage = imageUrl;
      if (isNew && imageUrl && result.productTypeID) {
        try {
          await assignEntityImage('product-type', result.productTypeID, imageUrl);
        } catch (err) {
          finalImage = null;
          toast.error(getErrorMessage(err, 'The product type was saved, but its image could not be attached.'));
        }
      }
      toast.success(isNew ? 'Product type added' : 'Product type saved');
      onSaved(result, isNew, finalImage);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message = getErrorMessage(err, isNew ? 'The product type could not be added.' : 'The product type could not be saved.');
      if (getErrorCode(err) === typesSvc.DUPLICATE_PRODUCT_TYPE_NAME || status === 422) setNameError(message);
      else toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  // ── Options ──────────────────────────────────────────────────────────

  const creditOptions = useMemo(() => {
    const live = allTypes.filter(
      (t) => isCreditProductType(t) && !isDeletedProductType(t) && t.productTypeID !== productTypeId,
    );
    const out = live.map((t) => ({ id: t.productTypeID, label: `${(t.productTypeName ?? '').trim() || 'Product type'} (#${t.productTypeID})`, unavailable: false }));
    // A saved credit can point at a type no longer offered; keep it visible so it can be removed.
    for (const id of form?.equivalentCreditIds ?? []) {
      if (!out.some((o) => o.id === id)) {
        const t = allTypes.find((x) => x.productTypeID === id);
        out.push({ id, label: `${(t?.productTypeName ?? '').trim() || 'Product type'} (#${id})`, unavailable: true });
      }
    }
    return out;
  }, [allTypes, productTypeId, form?.equivalentCreditIds]);

  const planOptions = useMemo(() => {
    const out = planTypes.filter((p) => p.active).map((p) => ({ id: p.planTypeID, label: p.name || `Plan type #${p.planTypeID}` }));
    for (const id of form?.planTypeIds ?? []) {
      if (!out.some((o) => o.id === id)) {
        const p = planTypes.find((x) => x.planTypeID === id);
        out.push({ id, label: p?.name || `Plan type #${id}` });
      }
    }
    return out;
  }, [planTypes, form?.planTypeIds]);

  const categoryOptions = useMemo(() => {
    const out = categories.map((c) => ({
      id: Number(c.productCategoryID),
      label: (c.productCategoryName ?? '').trim() || `Category #${c.productCategoryID}`,
    }));
    for (const id of form?.categoryIds ?? []) {
      if (!out.some((o) => o.id === id)) out.push({ id, label: `Category #${id}` });
    }
    return out;
  }, [categories, form?.categoryIds]);

  function toggleSection(key: 'products' | 'appts') {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const footer = locked ? (
    <DialogFooterLayout>
      <div className="flex-1" />
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </DialogFooterLayout>
  ) : (
    <DialogFooterLayout errors={requiredErrors} isCreating={isNew}>
      {dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={revertAll}
          >
            Revert
          </Button>
        </div>
      )}
      <div className="flex-1" />
      <Button variant="secondary" onClick={requestClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        aria-label={isNew ? 'Create product type' : 'Update product type'}
        onClick={() => void handleSave()}
        loading={saving}
        disabled={form === null || requiredErrors.length > 0}
      >
        {isNew ? 'Create' : 'Update'}
      </Button>
    </DialogFooterLayout>
  );

  const activeProducts = products ?? [];

  return (
    <Modal
      isOpen
      onClose={requestClose}
      title={
        <DialogHeading
          title={isNew ? 'Add Product Type' : locked ? 'Product Type' : 'Edit Product Type'}
          variant={isNew || locked ? 'default' : 'edit'}
          description={
            isNew
              ? 'Create a new product type with credits, plan types and categories.'
              : locked
                ? 'Type name, credits, plan types and categories.'
                : 'Update the product type. Type name, credits, plan types and categories.'
          }
        />
      }
      size="xl"
      closeOnEscape={false}
      footer={footer}
    >
      {form === null ? (
        detailFailed ? (
          <p className="text-sm text-slate-500">This product type couldn't be loaded. Close and reopen it to try again.</p>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-4 py-12">
            <Spinner />
            <p className="text-sm text-slate-500">Loading product type details...</p>
          </div>
        )
      ) : (
        <AdminLockedFieldset>
          <div className="grid gap-6">
            <div className="flex flex-col gap-6 md:flex-row">
              {/* Left column: image, then categories (corp Product Types dialog) */}
              <div className="space-y-4 md:w-48 md:shrink-0">
                <EntityImageTile
                  entityType="product-type"
                  entityId={productTypeId}
                  imageUrl={imageUrl}
                  label="Product Image"
                  onSelected={(url) => {
                    setImageUrl(url);
                    if (productTypeId !== null) onImageChanged?.(productTypeId, url);
                  }}
                />

                {/* Picked from the read-only category list (managed in the corporate
                    dashboard); the chosen ones are listed under it, removable. */}
                <div className="space-y-2">
                  <label htmlFor="pt-categories" className={LABEL_CLASS}>
                    Categories
                  </label>
                  <SearchableMultiSelect
                    id="pt-categories"
                    options={categoryOptions.map((o) => ({ value: String(o.id), label: o.label }))}
                    selectedValues={form.categoryIds.map(String)}
                    onSelectionChange={(values) => patch({ categoryIds: values.map(Number) })}
                    placeholder="No categories"
                    searchPlaceholder="Search categories…"
                  />
                  <SelectedRows
                    rows={form.categoryIds.map((id) => categoryOptions.find((o) => o.id === id) ?? { id, label: `Category #${id}` })}
                    removeLabel="Remove"
                    onRemove={(id) => patch({ categoryIds: toggleId(form.categoryIds, id, false) })}
                  />
                </div>
              </div>

              {/* Right column: the corp product type fields */}
              <div className="grid min-w-0 flex-1 gap-4">
                <DialogField
                  label="Type Name"
                  htmlFor="product-type-name"
                  required
                  state={nameState}
                  labelHint={blankName ? '— required' : shownNameError ? '— name already used' : undefined}
                >
                  <Input
                    id="product-type-name"
                    autoFocus={isNew}
                    placeholder="Product type name"
                    value={form.name}
                    onChange={(e) => patch({ name: e.target.value })}
                    error={shownNameError}
                    className={fieldClasses.input(nameState)}
                  />
                </DialogField>

                <DialogField label="Type" htmlFor="product-type-kind">
                  <Select<0 | 1>
                    id="product-type-kind"
                    options={TYPE_OPTIONS}
                    value={form.type}
                    onChange={(v) => patch({ type: v ?? 0 })}
                  />
                </DialogField>

                <DialogField
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      Disbursal Type
                      <InfoTip text="Appointment Disbursal (Recommended): products are disbursed when an appointment is checked out, with a full audit trail. Payment Disbursal (Retail): products are disbursed immediately when a payment is made; cannot be attached to appointment types." />
                    </span>
                  }
                  htmlFor="product-type-disbursal"
                  description={
                    form.disburseType === 1
                      ? 'Products are disbursed immediately when a payment is made. Cannot be attached to appointment types.'
                      : 'Products are disbursed when an appointment is checked out, providing a full audit trail of what products were used for each service.'
                  }
                >
                  <Select<0 | 1>
                    id="product-type-disbursal"
                    options={DISBURSE_OPTIONS}
                    value={form.disburseType}
                    onChange={changeDisburse}
                  />
                </DialogField>

                {form.type === 0 && (
                  <div className="space-y-2 rounded-md border border-slate-200 p-3">
                    <label htmlFor="pt-credits" className={LABEL_CLASS}>
                      <span className="inline-flex items-center gap-1.5">
                        Equivalent Credits
                        <InfoTip text="Makes this product interchangeable with a credit type. Products that share the same equivalent credit can be used in place of one another." />
                      </span>
                    </label>
                    {creditOptions.length === 0 ? (
                      <p className="text-xs text-slate-400">This account has no credit types.</p>
                    ) : (
                      <>
                        <SearchableMultiSelect
                          id="pt-credits"
                          options={creditOptions.filter((o) => !o.unavailable).map((o) => ({ value: String(o.id), label: o.label }))}
                          selectedValues={form.equivalentCreditIds.map(String)}
                          onSelectionChange={(values) => patch({ equivalentCreditIds: values.map(Number) })}
                          placeholder="No equivalent credits"
                          searchPlaceholder="Search credits…"
                        />
                        <SelectedRows
                          rows={form.equivalentCreditIds.map((id) => creditOptions.find((o) => o.id === id) ?? { id, label: `Product type (#${id})`, unavailable: true })}
                          removeLabel="Remove"
                          onRemove={(id) => patch({ equivalentCreditIds: toggleId(form.equivalentCreditIds, id, false) })}
                        />
                      </>
                    )}
                  </div>
                )}

                <DialogField label="Description" htmlFor="product-type-description">
                  <RichDescriptionField
                    id="product-type-description"
                    value={form.description}
                    onChange={(html) => patch({ description: html })}
                    dialogTitle="Edit Product Type Description"
                  />
                </DialogField>

                <div className="space-y-2 rounded-md border border-slate-200 p-3">
                  <label htmlFor="pt-plans" className={LABEL_CLASS}>
                    <span className="inline-flex items-center gap-1.5">
                      Plan Types
                      <InfoTip text="Links this product type to specific plans. When a patient has a matching plan, these products will appear on it for easy tracking." />
                    </span>
                  </label>
                  {planOptions.length === 0 ? (
                    <p className="text-xs text-slate-400">This account has no plan types.</p>
                  ) : (
                    <>
                      <SearchableMultiSelect
                        id="pt-plans"
                        options={planOptions.map((o) => ({ value: String(o.id), label: o.label }))}
                        selectedValues={form.planTypeIds.map(String)}
                        onSelectionChange={(values) => patch({ planTypeIds: values.map(Number) })}
                        placeholder="No plan types"
                        searchPlaceholder="Search plan types…"
                      />
                      <SelectedRows
                        rows={form.planTypeIds.map((id) => planOptions.find((o) => o.id === id) ?? { id, label: `Plan type #${id}` })}
                        removeLabel="Remove"
                        onRemove={(id) => patch({ planTypeIds: toggleId(form.planTypeIds, id, false) })}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {showAttachments && !isNew && (
              <div className="space-y-2 border-t border-slate-200 pt-2">
                <section aria-label="Attached products">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 text-left"
                    aria-expanded={openSections.has('products')}
                    onClick={() => toggleSection('products')}
                  >
                    {openSections.has('products') ? (
                      <ChevronDown className="h-4 w-4 text-slate-500" aria-hidden />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-500" aria-hidden />
                    )}
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                      <Package className="h-3.5 w-3.5 text-blue-600" aria-hidden />
                      Attached Products
                      <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 text-[10px] font-semibold text-blue-700">
                        {activeProducts.length}
                      </span>
                    </span>
                  </button>
                  {openSections.has('products') && (
                    <div className="ml-6 mt-1 space-y-1">
                      {products === null ? (
                        <Spinner size="sm" />
                      ) : activeProducts.length === 0 ? (
                        <p className="text-xs italic text-slate-400">No products attached</p>
                      ) : (
                        activeProducts.map((p) => (
                          <div
                            key={p.quoteItemCostID}
                            className="flex items-center gap-2 rounded border border-blue-100 bg-blue-50/50 px-2 py-1 text-xs"
                          >
                            <Package className="h-3 w-3 shrink-0 text-blue-500" aria-hidden />
                            <span className="min-w-0 flex-1 truncate">{p.name}</span>
                            {p.categoryName && <span className="max-w-[120px] truncate text-slate-400">{p.categoryName}</span>}
                            <span className="shrink-0 text-slate-400">{formatCurrency(parseCurrency(p.price))}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </section>
                <section aria-label="Attached appointment types">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 text-left"
                    aria-expanded={openSections.has('appts')}
                    onClick={() => toggleSection('appts')}
                  >
                    {openSections.has('appts') ? (
                      <ChevronDown className="h-4 w-4 text-slate-500" aria-hidden />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-500" aria-hidden />
                    )}
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                      <CalendarCheck className="h-3.5 w-3.5 text-orange-600" aria-hidden />
                      Attached Appt Types
                      <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-1.5 text-[10px] font-semibold text-orange-700">
                        {attachedAppointmentTypes.length}
                      </span>
                    </span>
                  </button>
                  {openSections.has('appts') && (
                    <div className="ml-6 mt-1 space-y-1">
                      {attachedAppointmentTypes.length === 0 ? (
                        <p className="text-xs italic text-slate-400">No appointment types attached</p>
                      ) : (
                        attachedAppointmentTypes.map((at) => (
                          <div
                            key={at.appointmentTypeID}
                            className="flex items-center gap-2 rounded border border-orange-100 bg-orange-50/50 px-2 py-1 text-xs"
                          >
                            <CalendarCheck className="h-3 w-3 shrink-0 text-orange-500" aria-hidden />
                            <span className="min-w-0 flex-1 truncate">{at.name}</span>
                            {at.via !== 'required' && (
                              <span className="shrink-0 rounded-full border border-purple-200 bg-purple-50 px-1.5 text-[10px] font-semibold text-purple-700">
                                {at.via === 'both' ? 'Required + Credit' : 'Service Credit'}
                              </span>
                            )}
                            {at.groupName && <span className="max-w-[140px] truncate text-slate-400">{at.groupName}</span>}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>
        </AdminLockedFieldset>
      )}

      <ConfirmDialog
        isOpen={confirmRetail}
        onClose={() => setConfirmRetail(false)}
        onConfirm={() => patch({ disburseType: 1 })}
        title="Switch to Payment Disbursal?"
        message={
          <>
            {RETAIL_DISBURSAL_WARNING.map((line) => (
              <span key={line} className="mb-2 block last:mb-0">
                {line}
              </span>
            ))}
          </>
        }
        confirmLabel="Switch to Payment Disbursal"
        variant="warning"
      />

      <ConfirmDialog
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={onClose}
        title="Discard changes?"
        message="You have unsaved changes. Close without saving them?"
        confirmLabel="Discard changes"
        variant="warning"
      />
    </Modal>
  );
}
